import { request } from "./http";

export interface LanguageInfo {
  code: string;
  name: string;
  native_name: string;
  levels: string[];
  published_courses: number;
}

export interface CourseInfo {
  id: string;
  language_code: string;
  level_code: string;
  title: string;
  description: string | null;
  is_published: boolean;
  /** False when this account can't open the level yet (not bought, or not
   *  assigned to this pupil). The catalogue still lists it. */
  unlocked?: boolean;
}

export interface LessonSummary {
  id: string;
  slug: string;
  title: string;
  theme: string | null;
  xp_reward: number;
  sort_order: number;
  image_url: string | null;
  completed: boolean;
  /** The one lesson of this course that is open to everyone. */
  is_free?: boolean;
}

export interface ChapterInfo {
  id: string;
  title: string;
  sort_order: number;
  lessons: LessonSummary[];
}

export interface CourseProgress {
  course_id: string;
  language_code: string;
  level_code: string;
  lessons_total: number;
  lessons_completed: number;
  avg_score: number;
  xp_earned: number;
  mastery_percent: number;
}

export interface CourseTree extends CourseInfo {
  chapters: ChapterInfo[];
  enrolled: boolean;
  progress: CourseProgress | null;
  /** True when this is the TASTER view: the level has not been bought, so
   *  the server withheld the syllabus and sent only the free lesson. */
  taster?: boolean;
}

export interface ActivityInfo {
  id: string;
  type: string;
  sort_order: number;
  payload: Record<string, unknown>;
}

export interface VocabEntry {
  german: string;
  english: string;
  example?: string;
}

export interface LessonDetail {
  id: string;
  slug: string;
  title: string;
  theme: string | null;
  xp_reward: number;
  image_url: string | null;
  vocab: VocabEntry[];
  activities: ActivityInfo[];
}

export interface LessonResult {
  lesson_id: string;
  status: string;
  score_percent: number | null;
  xp_earned: number;
  attempts: number;
  completed_at: string | null;
}

export function listLanguages(): Promise<LanguageInfo[]> {
  return request("/learning/languages");
}

export function listCourses(language?: string): Promise<CourseInfo[]> {
  return request("/learning/courses", { params: { language } });
}

export function courseTree(courseId: string): Promise<CourseTree> {
  return request(`/learning/courses/${courseId}`);
}

export function enrollInCourse(courseId: string): Promise<CourseProgress> {
  return request(`/learning/courses/${courseId}/enroll`, { method: "POST" });
}

export function lessonDetail(lessonId: string): Promise<LessonDetail> {
  return request(`/learning/lessons/${lessonId}`);
}

export function completeLesson(lessonId: string, scorePercent: number): Promise<LessonResult> {
  return request(`/learning/lessons/${lessonId}/complete`, {
    method: "POST",
    body: { score_percent: scorePercent },
  });
}

export function myProgress(): Promise<CourseProgress[]> {
  return request("/learning/my-progress");
}

/** First not-yet-completed lesson in tree order — the "continue where
 * you left off" target. Null when everything is done (or empty). */
export function nextLesson(tree: CourseTree): LessonSummary | null {
  for (const chapter of tree.chapters) {
    for (const lesson of chapter.lessons) {
      if (!lesson.completed) return lesson;
    }
  }
  return null;
}

/** Diamonds, and what earned them.
 *
 *  Derived server-side from achievements already recorded, so there is no
 *  balance to go stale here and nothing to keep in sync — refetching is
 *  always correct. */
export interface RewardBucket {
  /** How many of this thing the learner has finished. */
  done: number;
  /** What one of them is worth. Shown, so the next diamond is predictable. */
  each: number;
  diamonds: number;
}

export interface Rewards {
  total: number;
  lessons: RewardBucket;
  teile: RewardBucket;
  levels: RewardBucket;
}

export function myRewards(): Promise<Rewards> {
  return request("/learning/rewards");
}
